/**
 * Staggered cantilever design (research/taxonomy.md §1.4): the parameter block kept in
 * `design.params`, its presets ("alternate" fills the per-tier values from the tier count)
 * and the change functions the Shape step calls.
 *
 * Geometry is stored as proportions (x as fractions of W, tier heights as weights), so W, H
 * and the stock can change without refitting anything; the generator snaps to whole mm.
 */
import type { Appearance, Design } from '../../domain/schemas';
import { mm } from '../../domain/units';
import type { BaseParams } from '../base';

export const STAGGERED_ID = 'staggered-cantilever';
export const STAGGERED_VERSION = '1.0';
export const STAGGERED_LABEL = 'Staggered shelf';
export const STAGGERED_STOCK = 'sheesham-board-25';

export type BackBlock = 'none' | 'left-of-divider' | 'right-of-divider' | 'full';

export interface TierEnds {
  leftPanel: boolean;
  rightPanel: boolean;
  backBlock: BackBlock;
  leftPost: boolean;
  rightPost: boolean;
}

export interface Horizontal { xStart: number; xEnd: number }

/**
 * How the inner uprights near the tier ends (`leftPost`, `rightPost`) are built (FND-25):
 * `post` = a slim post `postDepth` deep at the front (the first slice's reading, the default for
 * designs without the key); `panel` = a full-depth panel, as most photographed S-shelves have.
 * Fitted designs are `panel` unless the photo clearly shows slim posts.
 */
export type VerticalKind = 'post' | 'panel';

export interface StaggeredParams extends BaseParams {
  tiers: number;
  /** Clear tier heights as weights, bottom-up; null = equal tiers. */
  tierWeights: number[] | null;
  /** Centre-line of the continuous divider over W; null = no divider. */
  dividerX: number | null;
  /** tiers + 1 entries bottom-up (bottom, shelves, top); fractions of W. */
  horizontals: Horizontal[];
  tierEnds: TierEnds[];
  /** Back block width over W. */
  blockWidth: number;
  /** Distance from the outer end to a post's outer face, over W. */
  postInset: number;
  /** Post size front to back (ticks). */
  postDepth: number;
  preset: 'alternate' | 'free';
  /** Inner uprights near the tier ends: slim posts (absent = post) or full-depth panels. */
  verticalKind?: VerticalKind;
  /**
   * Per tier (bottom-up), the centre-lines over W of short full-depth dividers that stand on
   * that tier's shelf and stop under the next (FND-11); absent or [] = none.
   */
  tierDividers?: number[][];
}

export const DEFAULT_APPEARANCE: Readonly<Appearance> = Object.freeze({ name: 'Staggered shelf, my version', staging: 'studio', props: true });

/** The alternate pattern's short shelf ends (David's S-shelf). */
export const ALT_SHORT_START = 0.23;
export const ALT_SHORT_END = 0.75;

/**
 * "alternate" (the S-shelf, period three, from David's photo): tier ends cycle
 * both panels · left panel + right post · right panel + left post; the shelves between
 * cycle short-left · full · short-right; back blocks alternate left and right of the divider.
 */
export function alternate(tiers: number): Pick<StaggeredParams, 'horizontals' | 'tierEnds'> {
  const ends: TierEnds[] = Array.from({ length: tiers }, (_x, i) => {
    const k = i % 3;
    const backBlock: BackBlock = i % 2 === 0 ? 'left-of-divider' : 'right-of-divider';
    if (k === 0) return { leftPanel: true, rightPanel: true, backBlock, leftPost: false, rightPost: false };
    if (k === 1) return { leftPanel: true, rightPanel: false, backBlock, leftPost: false, rightPost: true };
    return { leftPanel: false, rightPanel: true, backBlock, leftPost: true, rightPost: false };
  });
  const horizontals: Horizontal[] = Array.from({ length: tiers + 1 }, (_x, k) => {
    if (k === 0 || k === tiers) return { xStart: 0, xEnd: 1 };
    const m = (k - 1) % 3;
    return m === 0 ? { xStart: ALT_SHORT_START, xEnd: 1 } : m === 1 ? { xStart: 0, xEnd: 1 } : { xStart: 0, xEnd: ALT_SHORT_END };
  });
  return { horizontals, tierEnds: ends };
}

export function defaultParams(tiers = 5): StaggeredParams {
  return { tiers, tierWeights: null, dividerX: 0.475, ...alternate(tiers), blockWidth: 0.2, postInset: 0.1, postDepth: mm(40), preset: 'alternate', verticalKind: 'post', base: 'none' };
}

/** The parameter block of a staggered design (defaults when missing). */
export function paramsOf(d: Pick<Design, 'params'>): StaggeredParams {
  const p = (d.params ?? {}) as Partial<StaggeredParams>;
  const base = defaultParams(typeof p.tiers === 'number' ? p.tiers : 5);
  return { ...base, ...p } as StaggeredParams;
}

export type StaggeredPresetKey = 'david' | 'small-s' | 'tall-ply';

export const STAGGERED_PRESETS: Readonly<Record<StaggeredPresetKey, { label: string; description: string; W: number; H: number; D: number; stock: string; tiers: number }>> = Object.freeze({
  david: { label: 'Sheesham S-shelf 1000 × 1800', description: '1000 × 1800 × 300, five tiers, alternate', W: mm(1000), H: mm(1800), D: mm(300), stock: STAGGERED_STOCK, tiers: 5 },
  'small-s': { label: 'Small S 450 × 600', description: '450 × 600 × 300, three tiers', W: mm(450), H: mm(600), D: mm(300), stock: STAGGERED_STOCK, tiers: 3 },
  'tall-ply': { label: 'Birch ply 800 × 1400', description: '800 × 1400 × 280, four tiers', W: mm(800), H: mm(1400), D: mm(280), stock: 'birch-ply-18', tiers: 4 },
});

export function createStaggered(key: StaggeredPresetKey = 'david'): Design {
  const p = STAGGERED_PRESETS[key] ?? STAGGERED_PRESETS.david;
  return {
    family: STAGGERED_ID, W: p.W, H: p.H, D: p.D, n: 0, even: true, openings: [],
    stockProfileId: p.stock, constructionProfileId: 'staggered-cantilever-joints', appearance: { ...DEFAULT_APPEARANCE },
    params: { ...defaultParams(p.tiers) } as unknown as Record<string, unknown>,
  };
}

const withParams = (d: Design, p: StaggeredParams): Design => ({ ...d, appearance: { ...d.appearance }, params: JSON.parse(JSON.stringify(p)) as Record<string, unknown> });

/** Change the tier count: the "alternate" pattern refills every tier; equal tiers; per-tier dividers are cleared. */
export function setTiers(d: Design, tiers: number): Design {
  const p = paramsOf(d);
  const n = Math.max(2, Math.min(8, Math.round(tiers)));
  const next: StaggeredParams = { ...p, tiers: n, tierWeights: null, ...alternate(n), preset: 'alternate' };
  if (p.tierDividers) next.tierDividers = Array.from({ length: n }, () => []);
  return withParams(d, next);
}

/** Apply a preset to the current tier count. */
export function applyPreset(d: Design, preset: 'alternate'): Design {
  const p = paramsOf(d);
  return withParams(d, { ...p, ...alternate(p.tiers), preset });
}

/** Set one field of one tier's ends (the per-tier rows). */
export function setTierEnd<K extends keyof TierEnds>(d: Design, tier: number, key: K, value: TierEnds[K]): Design {
  const p = paramsOf(d);
  if (tier < 0 || tier >= p.tiers) return d;
  const tierEnds = p.tierEnds.map((e, i) => (i === tier ? { ...e, [key]: value } : e));
  return withParams(d, { ...p, tierEnds, preset: 'free' });
}

/** Set one horizontal's extent (fractions of W). */
export function setHorizontal(d: Design, k: number, h: Horizontal): Design {
  const p = paramsOf(d);
  if (k < 0 || k > p.tiers) return d;
  return withParams(d, { ...p, horizontals: p.horizontals.map((x, i) => (i === k ? { ...h } : x)), preset: 'free' });
}

/** Set the tier clear heights as weights (null = equal). */
export function setTierWeights(d: Design, weights: number[] | null): Design {
  return withParams(d, { ...paramsOf(d), tierWeights: weights ? weights.slice() : null });
}

export function setDividerX(d: Design, x: number | null): Design {
  return withParams(d, { ...paramsOf(d), dividerX: x });
}
